import type { UserSession } from '@/store/slices/userSessions';

import { parseUserAgent } from './device';

/**
 * One device/browser the account is signed in from — the "main" login plus every
 * refresh session descended from it.
 *
 * Background: the app refreshes the auth token on every page load, and each
 * refresh mints a NEW session row. A login records the device in
 * `token_data.user_agent`; a refresh records none. So the raw list is one real
 * login (with a user agent) trailed by user-agent-less refreshes. We collapse
 * that trail back onto the device: rows are one-per-distinct-user-agent (a
 * refresh never makes its own row), and the row's "Log out" revokes EVERY token
 * — main login + refreshes — so the device really goes away.
 *
 * Linking a given refresh to a specific login is a heuristic (the API exposes no
 * parent/session id). It is cosmetically irrelevant — refreshes are collapsed
 * counts, the row set and names depend only on the logins, and "Log out all"
 * (or per-device logout) still revokes every token. The ONE thing that must be
 * exact is which row is "This device": the current token is pinned to the device
 * whose user agent matches the live browser, falling back to its own row.
 */
export type SessionGroup = {
  /** Representative session for display (the login, with its device + geo). */
  display: UserSession;
  /** Every token in the group — main login + refreshes, revoked together. */
  tokens: string[];
  /** Most-recent activity in the group (drives ordering). */
  createdAt?: string;
  /** True when any token in the group is the one this browser authenticated with. */
  isCurrent: boolean;
};

const createdMs = (s: UserSession): number => {
  const ms = s.created_at ? Date.parse(s.created_at) : NaN;
  return Number.isNaN(ms) ? NaN : ms;
};

/** Expired only when there's a parseable `expires_at` in the past. */
const isExpired = (s: UserSession): boolean => {
  if (!s.expires_at) return false;
  const ms = Date.parse(s.expires_at);
  return !Number.isNaN(ms) && ms <= Date.now();
};

const userAgentOf = (s: UserSession): string | null => s.token_data?.user_agent ?? null;

/** Two user agents describe the same device/browser (lenient — version drift is fine). */
const sameDevice = (a: string, b: string): boolean => {
  const da = parseUserAgent(a);
  const db = parseUserAgent(b);
  return da.name === db.name && da.browser === db.browser;
};

/** Metadata richness — a login carrying a user agent (and ideally an IP) is the
 *  best representative; a bare refresh is the worst. */
const richness = (s: UserSession): number =>
  (s.token_data?.user_agent ? 2 : 0) + (s.token_data?.ip ? 1 : 0);

/** The member best worth showing: richest metadata, newest on a tie. */
const pickDisplay = (members: UserSession[]): UserSession =>
  members.reduce((best, s) => {
    if (richness(s) > richness(best)) return s;
    if (richness(s) === richness(best)) {
      const a = createdMs(s);
      const b = createdMs(best);
      if (!Number.isNaN(a) && (Number.isNaN(b) || a > b)) return s;
    }
    return best;
  }, members[0]);

const newestCreatedAt = (members: UserSession[]): string | undefined =>
  members.reduce<{ at: string | undefined; ms: number }>(
    (best, s) => {
      const ms = createdMs(s);
      return !Number.isNaN(ms) && ms > best.ms ? { at: s.created_at, ms } : best;
    },
    { at: undefined, ms: -Infinity },
  ).at;

const makeGroup = (members: UserSession[], currentToken: string | null): SessionGroup => {
  const tokens = members.map((m) => m.token);
  return {
    display: pickDisplay(members),
    tokens,
    createdAt: newestCreatedAt(members),
    isCurrent: currentToken != null && tokens.includes(currentToken),
  };
};

/** The anchor (login) created most recently at-or-before `r`, else the earliest
 *  one after it — i.e. the login whose refresh chain `r` most likely belongs to. */
const ownerOf = (r: UserSession, anchors: UserSession[]): UserSession => {
  const rt = createdMs(r);
  if (Number.isNaN(rt)) return anchors[0];
  let before: UserSession | null = null;
  let after: UserSession | null = null;
  for (const a of anchors) {
    const at = createdMs(a);
    if (Number.isNaN(at)) continue;
    if (at <= rt && (!before || at > createdMs(before))) before = a;
    if (at > rt && (!after || at < createdMs(after))) after = a;
  }
  return before ?? after ?? anchors[0];
};

/**
 * Collapse the raw session list into one group per device/browser: drops expired
 * tokens, groups logins by user agent, folds each refresh onto a login, pins the
 * current token to the device matching `currentUserAgent`, and orders the result
 * current-device-first then by most-recent activity.
 */
export const groupSessions = (
  sessions: UserSession[],
  currentToken: string | null,
  currentUserAgent?: string,
): SessionGroup[] => {
  const active = sessions.filter((s) => !isExpired(s));
  if (active.length === 0) return [];

  const anchors = active.filter((s) => userAgentOf(s) !== null);
  const refreshes = active.filter((s) => userAgentOf(s) === null);

  // No login carries a device at all → one collapsed group (the current row
  // falls back to the live navigator user agent for its name).
  if (anchors.length === 0) {
    return [makeGroup(active, currentToken)];
  }

  // Device buckets keyed by user agent (the device/browser fingerprint).
  const byDevice = new Map<string, UserSession[]>();
  for (const a of anchors) {
    const key = userAgentOf(a) as string;
    const arr = byDevice.get(key);
    if (arr) arr.push(a);
    else byDevice.set(key, [a]);
  }

  // The device key the CURRENT browser belongs to — the bucket whose UA matches
  // the live navigator UA (so "This device" is always the right row). `null`
  // when no login matches (this browser's login has expired/dropped off).
  const currentDeviceKey =
    currentToken != null && currentUserAgent
      ? ([...byDevice.keys()].find((ua) => sameDevice(ua, currentUserAgent)) ?? null)
      : null;

  // Current token that can't be matched to a device gets its own "This device"
  // row rather than being folded into someone else's device.
  const orphanCurrent: UserSession[] = [];

  for (const r of refreshes) {
    if (r.token === currentToken) {
      if (currentDeviceKey) (byDevice.get(currentDeviceKey) as UserSession[]).push(r);
      else orphanCurrent.push(r);
      continue;
    }
    const owner = userAgentOf(ownerOf(r, anchors)) as string;
    (byDevice.get(owner) as UserSession[]).push(r);
  }

  const groups = [...byDevice.values()].map((members) => makeGroup(members, currentToken));
  if (orphanCurrent.length) groups.push(makeGroup(orphanCurrent, currentToken));

  const at = (createdAt: string | undefined): number => {
    const ms = createdAt ? Date.parse(createdAt) : NaN;
    return Number.isNaN(ms) ? -Infinity : ms;
  };
  return groups.sort((a, b) => {
    if (a.isCurrent !== b.isCurrent) return a.isCurrent ? -1 : 1;
    return at(b.createdAt) - at(a.createdAt);
  });
};
